/**
 * BranchingSwitcher — the top-of-page variant toggle for the three
 * branching nav items (Orders / Billing / Settings).
 *
 * Per Plan 03 (canonical plan at apps/ordo/plans/plan-03-...md):
 *
 *   - Renders nothing (`null`) when the user has fewer than 2 variants —
 *     single-variant users see no misleading toggle.
 *   - Renders a 2-button "Personal / Work" strip when the user has 2
 *     variants (the common multi-hat case: PARENT + SCHOOL_ADMIN, etc.).
 *   - Renders the 3-button "Family / School / Kitchen" strip when the
 *     user has 3 variants (the ORDO_ADMIN panopticon case).
 *
 * The active variant is read off the URL (`/orders/family`, etc.) so
 * deep links carry the toggle state across reloads. Clicking a button
 * `navigate()`s to the next path while preserving search params.
 *
 * Three call sites (`/orders`, `/billing`, `/settings`) share this
 * implementation via the `kind` prop, dropping the plan's three
 * proposed component files into one helper. Pages still own the
 * variant-resolution logic (which variants to surface based on
 * profileCodes / enrollment funding) — this primitive only handles
 * the visual toggle + URL fork.
 */

import { ViewSwitcher } from '@ordo/ui/view-switcher'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router'

import { labelKeyFor, type SwitcherSurface } from './branching-switcher-label'

export { labelKeyFor } from './branching-switcher-label'

export type BranchingKind = 'orders' | 'billing' | 'settings'

export interface BranchingVariantOption {
  /** URL fragment appended after the base path (e.g. `family` → `/orders/family`). */
  urlFragment: string
  /** Semantic key — `family` / `school` / `kitchen` for Orders,
   *  `family-balance` / `school` / `kitchen` for Billing, etc. The
   *  switcher only needs the urlFragment + label, so this is opaque. */
  key: string
  /** Label-source hint: drives whether we show "Personal / Work" (2
   *  variants, consumer + staff hat) vs literal "Family / School /
   *  Kitchen" (3 variants, ORDO_ADMIN). For 2-variant mode the
   *  switcher labels are derived from `urlFragment` (family → Personal,
   *  school/kitchen → Work); for 3-variant mode we use the literal
   *  Family/School/Kitchen labels. */
  surface: SwitcherSurface
}

export interface BranchingSwitcherProps {
  kind: BranchingKind
  variants: BranchingVariantOption[]
}

export function BranchingSwitcher({ kind, variants }: BranchingSwitcherProps): React.ReactElement | null {
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()

  // Single-variant users see no toggle — going straight to the variant
  // is faster + clearer than a one-button strip that does nothing.
  if (variants.length < 2) {
    return null
  }

  const basePath = `/${kind}`
  const active = variants.find((v) => location.pathname.endsWith(`/${v.urlFragment}`)) ?? variants[0]!

  const views = variants.map((v) => {
    // `labelKeyFor` returns a bounded union of eight `switcher.*` literals,
    // every one of which is a catalog key — so the English copy lives in
    // `@ordo/i18n`'s `en.ts` instead of being re-declared at this call site.
    const key = labelKeyFor(v.surface, variants.length, kind)
    return { value: v.key, label: intl.formatMessage({ id: key }) }
  })

  return (
    <ViewSwitcher
      view={active.key}
      onViewChange={(nextKey) => {
        const next = variants.find((v) => v.key === nextKey)
        if (next) {
          navigate(`${basePath}/${next.urlFragment}${location.search}`, { replace: false })
        }
      }}
      views={views}
    />
  )
}
